import React, { useState, useEffect } from 'react';
import { useAppStore } from '../../stores/useAppStore';
import { useSettingsStore } from '../../stores/useSettingsStore';
import { useTranslation } from '../../i18n/LanguageContext';

interface SystemSettingsModalProps {
  visible: boolean;
  onClose: () => void;
}

const SystemSettingsModal: React.FC<SystemSettingsModalProps> = ({ visible, onClose }) => {
  const { audioSettings, updateAudioSettings } = useAppStore();
  const { weatherEnabled, setWeatherEnabled } = useSettingsStore();
  const { t, language, changeLanguage } = useTranslation();

  const [masterVolume, setMasterVolume] = useState(audioSettings.masterVolume);
  const [bgmVolume, setBgmVolume] = useState(audioSettings.bgmVolume);
  const [seVolume, setSeVolume] = useState(audioSettings.seVolume);

  useEffect(() => {
    if (visible) {
      setMasterVolume(audioSettings.masterVolume);
      setBgmVolume(audioSettings.bgmVolume);
      setSeVolume(audioSettings.seVolume);
    }
  }, [visible, audioSettings]);

  if (!visible) return null;

  const handleMasterVolumeChange = (value: number) => {
    setMasterVolume(value);
    updateAudioSettings({ masterVolume: value });
  };

  const handleBgmVolumeChange = (value: number) => {
    setBgmVolume(value);
    updateAudioSettings({ bgmVolume: value });
  };

  const handleSeVolumeChange = (value: number) => {
    setSeVolume(value);
    updateAudioSettings({ seVolume: value });
  };

  const sliderClass = `w-full h-2 bg-white/[0.08] rounded-lg appearance-none cursor-pointer
    [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4
    [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full
    [&::-webkit-slider-thumb]:bg-blue-400`;

  return (
    <div
      className="fixed inset-0 glass-overlay z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="glass-panel p-6 text-white max-w-md w-full max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-xl font-bold mb-6 flex-shrink-0">{t('menu.systemSettings')}</h2>

        <div className="space-y-6 flex-1 min-h-0 overflow-y-auto">
          {/* 音量設定 */}
          <div>
            <label className="block text-sm font-medium mb-2">
              {language === 'ja' ? '全体音量' : 'Master Volume'}: {Math.round(masterVolume * 100)}%
            </label>
            <input
              type="range" min="0" max="100"
              value={masterVolume * 100}
              onChange={(e) => handleMasterVolumeChange(Number(e.target.value) / 100)}
              className={sliderClass}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              {language === 'ja' ? 'BGM音量' : 'BGM Volume'}: {Math.round(bgmVolume * 100)}%
            </label>
            <input
              type="range" min="0" max="100"
              value={bgmVolume * 100}
              onChange={(e) => handleBgmVolumeChange(Number(e.target.value) / 100)}
              className={sliderClass}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              {language === 'ja' ? 'SE音量' : 'SE Volume'}: {Math.round(seVolume * 100)}%
            </label>
            <input
              type="range" min="0" max="100"
              value={seVolume * 100}
              onChange={(e) => handleSeVolumeChange(Number(e.target.value) / 100)}
              className={sliderClass}
            />
          </div>

          <div className="border-t border-white/[0.08] pt-4"></div>

          {/* 言語設定 */}
          <div>
            <label className="block text-sm font-medium mb-2">
              {t('settings.language')}
            </label>
            <div className="flex gap-2">
              <button
                onClick={() => changeLanguage('ja')}
                className={`flex-1 py-2 px-4 rounded-md transition-colors ${
                  language === 'ja'
                    ? 'bg-blue-500/50 border border-blue-500/30 text-white'
                    : 'bg-white/[0.06] border border-white/[0.08] text-white/60 hover:bg-white/[0.1]'
                }`}
              >
                日本語
              </button>
              <button
                onClick={() => changeLanguage('en')}
                className={`flex-1 py-2 px-4 rounded-md transition-colors ${
                  language === 'en'
                    ? 'bg-blue-500/50 border border-blue-500/30 text-white'
                    : 'bg-white/[0.06] border border-white/[0.08] text-white/60 hover:bg-white/[0.1]'
                }`}
              >
                English
              </button>
            </div>
          </div>

          <div className="border-t border-white/[0.08] pt-4"></div>

          {/* 天気設定 */}
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <label className="text-sm font-medium">
                {t('settings.weatherEnabled')}
              </label>
              <p className="text-xs text-white/40 mt-1">
                {language === 'ja'
                  ? '位置情報を使用して現在地の天気を表示します'
                  : 'Display weather based on your location'}
              </p>
            </div>
            <button
              onClick={() => setWeatherEnabled(!weatherEnabled)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                weatherEnabled ? 'bg-blue-500/60' : 'bg-white/[0.12]'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  weatherEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {weatherEnabled && (
            <div className="p-3 bg-blue-500/10 border border-blue-500/15 rounded-xl text-xs text-blue-200">
              <p>
                {language === 'ja'
                  ? '天気機能を有効にしました。'
                  : 'Weather feature enabled.'}
              </p>
              <p className="mt-1">
                {language === 'ja'
                  ? 'ブラウザが位置情報の許可を求めた場合は「許可」を選択してください。'
                  : 'Please allow location access when prompted by your browser.'}
              </p>
            </div>
          )}
        </div>

        <button
          onClick={onClose}
          className="mt-6 w-full py-2 px-4 glass-btn-primary transition-colors flex-shrink-0"
        >
          {t('common.close')}
        </button>
      </div>
    </div>
  );
};

export default SystemSettingsModal;
